import { SimpleGrid, Text, Stack } from '@repletoryreach/sdk-ui';
import { palette } from '@repletoryreach/sdk-utils/color';
import { useAppStore } from '@repletoryreach/sdk-utils/store';
import { useT } from '../../i18n/I18nProvider';

/** The hues offered as an accent. Each one is a real scale in the shared palette. */
type Hue = 'amber' | 'orange' | 'red' | 'pink' | 'violet' | 'indigo' | 'blue' | 'cyan' | 'teal' | 'green' | 'lime' | 'yellow';

interface Swatch { nameKey: string; value: Hue }
const SWATCHES: Swatch[] = [
  { nameKey: 'common.color.amber',  value: 'amber' },
  { nameKey: 'common.color.orange', value: 'orange' },
  { nameKey: 'common.color.red',    value: 'red' },
  { nameKey: 'common.color.pink',   value: 'pink' },
  { nameKey: 'common.color.violet', value: 'violet' },
  { nameKey: 'common.color.indigo', value: 'indigo' },
  { nameKey: 'common.color.blue',   value: 'blue' },
  { nameKey: 'common.color.cyan',   value: 'cyan' },
  { nameKey: 'common.color.teal',   value: 'teal' },
  { nameKey: 'common.color.green',  value: 'green' },
  { nameKey: 'common.color.lime',   value: 'lime' },
  { nameKey: 'common.color.yellow', value: 'yellow' }
];

/**
 * Accent (primary) picker.
 *
 * It used to write the hue's *name* into `useAppStore.primary`, which the theme
 * feeds straight into `--rr-color-primary` and its derived surface colours. A
 * name is not a CSS colour, so picking anything here left the console's primary
 * undefined rather than recoloured. It writes the 500 shade from the shared
 * palette now, the same form `main.tsx` seeds at boot.
 */
export function ColorSwatchPicker() {
  const t = useT();
  const primary = useAppStore((s) => s.primary);
  const setPrimary = useAppStore((s) => s.setPrimary);

  return (
    <Stack gap="xs">
      <Text fz="xs" c="var(--rr-color-text-muted)">{t('common.accentColor')}</Text>
      <SimpleGrid cols={6} spacing="xs">
        {SWATCHES.map((s) => {
          const colour = palette[s.value][500];
          const active = primary.toLowerCase() === colour.toLowerCase();
          const name = t(s.nameKey);
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setPrimary(colour)}
              aria-pressed={active}
              aria-label={t('common.setAccentTo', { name })}
              title={name}
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                border: active ? '2px solid var(--rr-color-text)' : '1px solid var(--rr-color-border)',
                // The swatch is the colour it sets, read from the palette's own
                // CSS variable rather than repeated as a literal here.
                background: `var(--rr-color-${s.value}-500)`,
                cursor: 'pointer',
                padding: 0,
                outline: 'none'
              }}
            />
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}
